import { FC, useEffect, useMemo, useRef } from "react";
import { startDownload } from "../utils";
import FileCardInternal from "./File";
import ImageCardInternal from "./Image";
import { FileCardProps } from "./types";
import { styles } from "./styles";

/**
 * FileCard Component
 *
 * Auto-detects whether to render as an image card or file card based on file type.
 * - Images (JPG, PNG, GIF, etc.) → 64×64 square card with thumbnail
 * - Files (PDF, DOCX, ZIP, etc.) → 224×64 horizontal card with icon
 */
const FileCard: FC<FileCardProps> = ({
  file,
  alt,
  error,
  loading,
  actions,
  onClick
}) => {
  const previousObjectURL = useRef<string | undefined>();

  const imgSrc = useMemo<string | false>(() => {
    if (previousObjectURL.current) {
      URL.revokeObjectURL(previousObjectURL.current);
    }
    if (!isImageFile(file)) {
      return false;
    }
    if (file instanceof File) {
      previousObjectURL.current = URL.createObjectURL(file);
      return previousObjectURL.current;
    }
    if (file.url) {
      return file.url;
    }
    return false;
  }, [file]);

  useEffect(() => {
    return () => {
      if (previousObjectURL.current) {
        URL.revokeObjectURL(previousObjectURL.current);
      }
    };
  }, []);

  const role = useMemo(
    () =>
      (onClick ? "button" : undefined) as typeof onClick extends (
        ...args: unknown[]
      ) => unknown
        ? "button"
        : undefined,
    [typeof onClick === "function"]
  );

  return (
    <div
      className="kit-FileCard"
      role={role}
      tabIndex={role === "button" ? 0 : -1}
      css={[
        imgSrc ? styles.imageContainer : styles.fileContainer,
        onClick && styles.clickable,
        !!error && styles.errorState
      ]}
      onClick={role === "button" ? onClick : undefined}
      onKeyDown={
        role === "button"
          ? e => {
              if (onClick && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
    >
      {imgSrc && (
        <ImageCardInternal
          src={imgSrc}
          imageName={file.name}
          alt={alt}
          loading={loading}
          error={error}
        />
      )}

      {!imgSrc && (
        <FileCardInternal
          fileName={file.name}
          loading={loading}
          error={error}
        />
      )}

      {actions && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className="kit-FileCard-actions"
          css={styles.actions}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          {actions}
        </div>
      )}
    </div>
  );
};

function isImageFile(file: File | { name: string; url?: string }): boolean {
  if (file instanceof File) {
    return file.type.startsWith("image/");
  }

  const imageExtensionRegex = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/i;

  if (file.url && imageExtensionRegex.test(file.url)) {
    return true;
  }

  return imageExtensionRegex.test(file.name);
}

export default FileCard;
export { startDownload as downloadFile };
export * from "./types";
